import { BaseService } from '@myhealth-raccoon/services';

import {
    AuthorizationRequest,
    CodeRequestType,
    PartialBookingUserWithToken,
    PatientCheckRequest,
    TokenResponse,
} from './types';

type TokenType = 'umbraco' | 'booking_guest' | 'booking_manage_guest' | 'booking_manage' | 'match' | 'patient';
type TokenWithScope = { jwt: string; token_type: TokenType };

class AuthService extends BaseService {
    constructor() {
        super('auth', import.meta.env.VITE_AUTH_API_URL);

        const querystring = new URLSearchParams(window.location.search);
        if (querystring.has('_booking')) {
            this.Token = { jwt: querystring.get('_booking')!, token_type: 'patient' };
        } else {
            this.TryGetTokenFromSessionStorage();
        }
    }

    private TryGetTokenFromSessionStorage(token_type?: TokenType): boolean {
        let jwt: string | null;
        switch (token_type) {
            case 'patient':
                jwt = window.sessionStorage.getItem('patient_jwt');
                if (jwt) {
                    this.Token = {
                        jwt,
                        token_type: 'patient',
                    };
                    return true;
                }
                return false;
            case 'match':
                jwt = window.sessionStorage.getItem('match_jwt');
                if (jwt) {
                    this.Token = {
                        jwt,
                        token_type: 'match',
                    };
                    return true;
                }
                return false;
            case 'booking_guest':
                jwt = window.sessionStorage.getItem('booking_guest_jwt');
                if (jwt) {
                    this.Token = {
                        jwt,
                        token_type: 'booking_guest',
                    };
                    return true;
                }
                return false;
            default:
                return (
                    this.TryGetTokenFromSessionStorage('patient') ||
                    this.TryGetTokenFromSessionStorage('match') ||
                    this.TryGetTokenFromSessionStorage('booking_guest')
                );
        }
    }

    private token?: TokenWithScope;

    get Authorization() {
        return this.token ? `Bearer ${this.token.jwt}` : undefined;
    }

    public set Token({ jwt, token_type }: TokenWithScope) {
        this.token = { jwt, token_type };
        sessionStorage.setItem(`${token_type}_jwt`, jwt);
    }

    get Token() {
        return this.token!;
    }

    public async ConnectBookingApp(siteId: string | number, isNewPatient: boolean) {
        if (this.Token.token_type !== 'umbraco' && !this.TryGetTokenFromSessionStorage('umbraco')) {
            throw 'no umbraco token to use';
        }

        const data = {
            grant_type: 'temporary_token',
            client_id: 'myhealth_booking_app',
            audiences: ['myhealth-bperp-api', 'myhealth-auth-api'],
            bp_site_id: siteId,
            user_claim_type: 'MyhealthBPPatient',
            is_new_patient: isNewPatient,
        };

        const formData = new URLSearchParams();
        for (const [key, value] of Object.entries(data)) {
            if (Array.isArray(value)) {
                for (const item of value) {
                    formData.append(key, item);
                }
            } else {
                formData.append(key, value!.toString());
            }
        }

        const response = await this.post<TokenResponse>('connect/token', formData);
        this.Token = { jwt: response.access_token, token_type: 'booking_guest' };
        return response;
    }

    public async ConnectManageBookingAppLite(appt_hash: string) {
        const data = {
            grant_type: 'temporary_token',
            client_id: 'myhealth_manage_booking_lite',
            audiences: ['myhealth-auth-api'],
            appt_hash,
        };

        const formData = new URLSearchParams();
        for (const [key, value] of Object.entries(data)) {
            if (Array.isArray(value)) {
                for (const item of value) {
                    formData.append(key, item);
                }
            } else {
                formData.append(key, value!.toString());
            }
        }

        const response = await this.post<TokenResponse>('connect/token', formData);
        this.Token = { jwt: response.access_token, token_type: 'booking_manage_guest' };
        return response;
    }

    public async BookingTempToken(hash: string) {
        const response = await this.post<TokenResponse>('manage-booking/access', hash);
        this.Token = { jwt: response.access_token, token_type: 'booking_manage' };
        return response;
    }

    public async Initial(patientCheck: PatientCheckRequest) {
        this.TryGetTokenFromSessionStorage('booking_guest');
        const response = await this.post<PartialBookingUserWithToken>(`booking/initial`, patientCheck);

        this.Token = { jwt: response.tokenResponse.access_token, token_type: 'match' };
        return response;
    }

    public async VerifyCode(verification_code: string, verification_contact_type: CodeRequestType) {
        const authRequest = {
            verification_code,
            verification_contact_type,
            grant_type: 'otp',
            client_secret: import.meta.env.VITE_AUTH_CLIENT_SECRET,
            client_id: 'myhealth_booking_app',
            audiences: ['myhealth-bperp-api', 'myhealth-auth-api', 'myhealth-comms-api'],
        } as AuthorizationRequest;

        const urlEncoded = new URLSearchParams();
        for (const [k, v] of Object.entries(authRequest)) {
            if (v === undefined || v === null) {
                continue;
            }
            if (Array.isArray(v)) {
                for (const w of v) {
                    urlEncoded.append(k, w);
                }
            } else {
                urlEncoded.append(k, `${v}`);
            }
        }

        const response = await this.post<TokenResponse>('connect/token', urlEncoded);

        this.Token = { jwt: response.access_token, token_type: 'patient' };
        sessionStorage.setItem('jwt', response.access_token);
        return response;
    }
}

export const authService = new AuthService();
export * from './types';
